import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// note-client-mod -- NoteEditor
//
// 笔记编辑弹窗：标题 + Markdown 正文 + 笔记本选择 + 标签 + 可见性 + 置顶/收藏。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { radius, spacing, typography } from '../styles/theme.js';
import { inputBase, buttonBase } from '../styles/animations.js';
import { CloseIcon } from './icons.js';
import { getNoteApiClient } from '../note-api-client.js';
export function NoteEditor({ note, notebooks, defaultNotebookId, onSuccess, onCancel }) {
    const { theme } = useTheme();
    const [title, setTitle] = React.useState(note?.title ?? '');
    const [content, setContent] = React.useState(note?.content ?? '');
    const [summary, setSummary] = React.useState(note?.summary ?? '');
    const [tagsText, setTagsText] = React.useState((note?.tags ?? []).join(', '));
    const [notebookId, setNotebookId] = React.useState(note?.notebookId ?? defaultNotebookId ?? '');
    const [visibility, setVisibility] = React.useState(note?.visibility ?? 'private');
    const [isPinned, setIsPinned] = React.useState(note?.isPinned ?? false);
    const [isFavorite, setIsFavorite] = React.useState(note?.isFavorite ?? false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const isEdit = Boolean(note);
    const handleSave = async () => {
        if (!title.trim()) {
            setError('请填写标题');
            return;
        }
        setSaving(true);
        setError(null);
        const params = {
            title: title.trim(),
            content,
            summary: summary.trim() || undefined,
            notebookId: notebookId || undefined,
            tags: tagsText
                .split(/[,，]/)
                .map(t => t.trim())
                .filter(Boolean),
            visibility,
            isPinned,
            isFavorite,
        };
        try {
            let result = null;
            const client = getNoteApiClient();
            if (isEdit && note) {
                result = await client.updateNote(note._id, params);
            }
            else {
                result = await client.createNote(params);
            }
            if (result) {
                onSuccess?.(result);
            }
            else {
                setError('保存失败');
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : '保存笔记失败');
        }
        finally {
            setSaving(false);
        }
    };
    const modalOverlay = {
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.bg.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    };
    const modalBox = {
        backgroundColor: theme.bg.elevated,
        borderRadius: radius.xl,
        padding: spacing.xl,
        width: '680px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: theme.shadow.xl,
        animation: 'note-scale-in 0.2s ease forwards',
    };
    const label = {
        display: 'block',
        marginBottom: spacing.xs,
        fontSize: typography.fontSize.sm,
        color: theme.text.secondary,
        fontWeight: typography.fontWeight.medium,
    };
    const input = inputBase(theme);
    const textarea = {
        ...inputBase(theme),
        minHeight: '220px',
        resize: 'vertical',
        lineHeight: typography.lineHeight.relaxed,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    };
    const selectStyle = {
        ...inputBase(theme),
        cursor: 'pointer',
    };
    const toggleChip = (active) => ({
        padding: '6px 12px',
        borderRadius: radius.full,
        border: `1px solid ${active ? theme.status.primary : theme.border.default}`,
        backgroundColor: active ? theme.interaction.selected : theme.bg.secondary,
        color: active ? theme.status.primary : theme.text.secondary,
        fontSize: typography.fontSize.sm,
        cursor: 'pointer',
        transition: 'background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease',
    });
    return (_jsx("div", { style: modalOverlay, onClick: onCancel, children: _jsxs("div", { style: modalBox, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.lg }, children: [_jsx("h3", { style: { margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary }, children: isEdit ? '编辑笔记' : '新建笔记' }), _jsx("button", { onClick: onCancel, style: buttonBase({ color: theme.text.tertiary }), title: "\u5173\u95ED", children: _jsx(CloseIcon, {}) })] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.md }, children: [_jsx("input", { placeholder: "\u7B14\u8BB0\u6807\u9898", value: title, onChange: (e) => setTitle(e.target.value), style: { ...input, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }, autoFocus: true }), _jsx("textarea", { placeholder: "\u652F\u6301 Markdown\uFF0C\u4F8B\u5982\uFF1A# \u6807\u9898\u3001**\u52A0\u7C97**\u3001- \u5217\u8868...", value: content, onChange: (e) => setContent(e.target.value), style: textarea }), _jsxs("label", { style: label, children: ["\u6458\u8981\uFF08\u53EF\u9009\uFF0C\u7559\u7A7A\u81EA\u52A8\u4ECE\u6B63\u6587\u63D0\u53D6\uFF09", _jsx("input", { placeholder: "\u4E00\u53E5\u8BDD\u6458\u8981", value: summary, onChange: (e) => setSummary(e.target.value), style: input })] }), _jsxs("div", { style: { display: 'flex', gap: spacing.md, flexWrap: 'wrap' }, children: [_jsx("div", { style: { flex: '1 1 200px' }, children: _jsxs("label", { style: label, children: ["\u7B14\u8BB0\u672C", _jsxs("select", { value: notebookId, onChange: (e) => setNotebookId(e.target.value), style: { ...selectStyle, width: '100%' }, children: [_jsx("option", { value: "", children: "\uFF08\u9ED8\u8BA4\u7B14\u8BB0\u672C\uFF09" }), notebooks.map(nb => (_jsx("option", { value: nb._id, children: nb.name }, nb._id)))] })] }) }), _jsx("div", { style: { flex: '1 1 200px' }, children: _jsxs("label", { style: label, children: ["\u53EF\u89C1\u6027", _jsxs("select", { value: visibility, onChange: (e) => setVisibility(e.target.value), style: { ...selectStyle, width: '100%' }, children: [_jsx("option", { value: "private", children: "\u79C1\u6709\uFF08\u4EC5\u81EA\u5DF1\u53EF\u89C1\uFF09" }), _jsx("option", { value: "public", children: "\u516C\u5F00\uFF08\u53EF\u5206\u4EAB\u94FE\u63A5\uFF09" })] })] }) })] }), _jsxs("label", { style: label, children: ["\u6807\u7B7E\uFF08\u9017\u53F7\u5206\u9694\uFF09", _jsx("input", { placeholder: "\u5982\uFF1A\u5DE5\u4F5C, \u7075\u611F, \u8BFB\u4E66\u7B14\u8BB0", value: tagsText, onChange: (e) => setTagsText(e.target.value), style: input })] }), _jsxs("div", { style: { display: 'flex', gap: spacing.sm }, children: [_jsxs("button", { type: "button", onClick: () => setIsPinned(v => !v), style: toggleChip(isPinned), children: ["\uD83D\uDCCC ", isPinned ? '已置顶' : '置顶'] }), _jsxs("button", { type: "button", onClick: () => setIsFavorite(v => !v), style: toggleChip(isFavorite), children: ["\u2B50 ", isFavorite ? '已收藏' : '收藏'] })] }), error && (_jsx("div", { style: { padding: spacing.sm, borderRadius: radius.md, backgroundColor: theme.status.dangerBg, color: theme.status.danger, fontSize: typography.fontSize.base }, children: error })), _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }, children: [_jsx("button", { onClick: onCancel, style: buttonBase({
                                        padding: '8px 16px',
                                        border: `1px solid ${theme.border.default}`,
                                        borderRadius: radius.md,
                                        backgroundColor: theme.bg.secondary,
                                        color: theme.text.secondary,
                                        fontSize: typography.fontSize.base,
                                    }), children: "\u53D6\u6D88" }), _jsx("button", { onClick: handleSave, disabled: saving, style: buttonBase({
                                        padding: '8px 20px',
                                        borderRadius: radius.md,
                                        backgroundColor: theme.status.primary,
                                        color: theme.text.inverse,
                                        fontSize: typography.fontSize.base,
                                        fontWeight: typography.fontWeight.medium,
                                        opacity: saving ? 0.6 : 1,
                                    }), children: saving ? '保存中...' : isEdit ? '保存修改' : '创建笔记' })] })] })] }) }));
}
//# sourceMappingURL=note-editor.js.map